import { useLocation } from "react-router";
import { useAccess } from "@/shared/api/grants";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { Dock, DockBar, DockHandle } from "@/shared/ui/layout/Dock";
import { activeDockEntry, visibleDockItems, type DockEntry } from "@/app/workspace/dock";
import { usePortalWorkInProgress } from "@/features/advice-portal/lib/portal-work";

export function ShellDock({
  folded,
  onFold,
  onUnfold,
  onSelect,
}: {
  folded: boolean;
  onFold: () => void;
  onUnfold: () => void;
  onSelect: (entry: DockEntry) => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const access = useAccess();
  const items = visibleDockItems(access);
  const active = activeDockEntry(pathname, items);
  const working = usePortalWorkInProgress();
  const Icon = active?.icon;

  return (
    <DockBar folded={folded}>
      {folded ? (
        <DockHandle
          current={active && Icon ? { label: t(active.labelKey), icon: <Icon /> } : null}
          label={t("shell.dock.menu")}
          unfoldLabel={t("shell.dock.unfold")}
          onUnfold={onUnfold}
        />
      ) : (
        <Dock
          label={t("shell.dock.label")}
          foldLabel={t("shell.dock.fold")}
          onFold={onFold}
          slots={items.map((item) =>
            item.kind === "separator"
              ? { kind: "separator", key: item.key }
              : {
                  kind: "entry",
                  key: item.key,
                  label:
                    working && item.resume
                      ? t("shell.dock.inProgress", { label: t(item.labelKey) })
                      : t(item.labelKey),
                  icon: item.icon,
                  active: item === active,
                  dot: working && item.resume !== undefined,
                  onSelect: () => onSelect(item),
                  tour: item.tour,
                },
          )}
        />
      )}
    </DockBar>
  );
}
